import {
  VStack,
  Box,
  Text,
  HStack,
  Icon,
  Link,
  Image,
} from '@chakra-ui/react';
import DOMPurify from 'dompurify';
import {  MdDescription, MdAccessTime } from 'react-icons/md';
import { Modal } from '@/client/components/shared/Modal';
import { formatters } from '@/client/utils/formatters';
import type { TaskNote } from '@/lib/sdk/chatbot/types';

interface NotesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  notes: TaskNote[];
  taskName: string;
  description?: string;
}

export function NotesDialog({ isOpen, onClose, notes, taskName, description }: NotesDialogProps) {
  const userNotes = notes
    .filter(note => !note.is_action && !note.is_custom_data)
    .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());

  const renderMedia = (note: TaskNote) => {
    if (!note.media_url) return null;

    const mimeType = note.mime_type?.toLowerCase() || '';

    if (mimeType.startsWith('image/')) {
      return (
        <Image
          src={note.media_url}
          alt={note.file_name || 'Photo'}
          maxH="300px"
          borderRadius="md"
          objectFit="contain"
          mt={2}
        />
      );
    }

    if (mimeType.startsWith('video/')) {
      return (
        <Box mt={2}>
          <video
            src={note.media_url}
            controls
            style={{ maxHeight: '300px', borderRadius: '8px', width: '100%' }}
          />
        </Box>
      );
    }

    if (mimeType.startsWith('audio/')) {
      return (
        <Box mt={2}>
          <audio src={note.media_url} controls style={{ width: '100%' }} />
        </Box>
      );
    }

    // Document fallback
    return (
      <HStack spacing={1} mt={2}>
        <Icon as={MdDescription} color="text-muted" boxSize={4} />
        <Link href={note.media_url} isExternal fontSize="sm" color="brand.500">
          {note.file_name || 'Document'}
        </Link>
      </HStack>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={taskName} size="lg">
      {description && (
        <Box mb={4} p={3} bg="bg-muted" borderRadius="md" borderLeftWidth="3px" borderLeftColor="brand.400">
          <Text fontSize="xs" fontWeight="semibold" color="text-secondary" textTransform="uppercase" letterSpacing="wide" mb={1}>
            Description
          </Text>
          <Text fontSize="sm" color="text-primary" whiteSpace="pre-wrap">
            {description}
          </Text>
        </Box>
      )}
      {userNotes.length === 0 ? (
        <Text color="text-secondary" fontSize="sm">No notes yet.</Text>
      ) : (
        <VStack spacing={3} align="stretch">
          {userNotes.map((note) => (
            <Box
              key={note.id}
              p={3}
              bg="bg-surface"
              borderRadius="md"
              borderWidth="1px"
              borderColor="border-default"
            >
              {note.status_text && (
                <Box
                  fontSize="sm"
                  color="text-primary"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(note.status_text) }}
                />
              )}
              {renderMedia(note)}
              {note.updated_at && (
                <HStack spacing={1} mt={2}>
                  <Icon as={MdAccessTime} color="text-muted" boxSize={3} />
                  <Text fontSize="xs" color="text-muted">
                    {formatters.relativeTime(note.updated_at)}
                  </Text>
                  {note.name && (
                    <Text fontSize="xs" color="text-muted">
                      &middot; {note.name}
                    </Text>
                  )}
                </HStack>
              )}
            </Box>
          ))}
        </VStack>
      )}
    </Modal>
  );
}